import { useState, type FormEvent } from "react";
import { SectionHeading, SectionShell } from "./brand";

const FORM_ADDR = "https://readdy.ai/api/form/dadqchbep053ijub13vg";

type Status = { type: "idle" | "success" | "error"; text: string };

export default function ContactForm() {
  const [status, setStatus] = useState<Status>({ type: "idle", text: "" });
  const [sending, setSending] = useState(false);
  const [len, setLen] = useState(0);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // 反垃圾蜜罐：若被自动填写则不发送，仅展示常规反馈
    const honeypot = String(fd.get("company_alt") || "").trim();
    if (honeypot) {
      setStatus({ type: "success", text: "已收到，我们会尽快与你联系。" });
      return;
    }

    const payload = new URLSearchParams();
    const name = String(fd.get("name") || "").trim();
    const email = String(fd.get("email") || "").trim();
    const subject = String(fd.get("subject") || "").trim();
    const message = String(fd.get("message") || "").trim();
    if (name) payload.append("name", name);
    if (email) payload.append("email", email);
    if (subject) payload.append("subject", subject);
    if (message) payload.append("message", message);

    if (!email || !subject || !message) {
      setStatus({ type: "error", text: "请填写联系邮箱、来意类型与详细描述后提交。" });
      return;
    }

    setSending(true);
    setStatus({ type: "idle", text: "" });
    try {
      const res = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const ok =
        res.ok &&
        (parsed?.code === "OK" || parsed?.code === undefined) &&
        !/spam/i.test(serverMsg);

      if (ok) {
        setStatus({
          type: "success",
          text: "提交成功！你的信息已进入我们的求证队列，请留意邮箱的后续进展。",
        });
        form.reset();
        setLen(0);
      } else {
        setStatus({
          type: "error",
          text: serverMsg || "提交失败，请稍后再试。",
        });
      }
    } catch {
      setStatus({ type: "error", text: "网络异常，提交失败，请稍后重试。" });
    } finally {
      setSending(false);
    }
  }

  const inputCls =
    "w-full rounded-xl border border-background-200 bg-background-50 px-4 py-3 text-sm outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100";

  return (
    <SectionShell id="contact">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_380px]">
        {/* 表单 */}
        <div>
          <SectionHeading
            eyebrow="我要求证 / 我来爆料"
            title="有一条瓜想让我们核验？直接说"
            desc="提交你想求证或补充的线索即可。匿名同样有效，越具体的可验证细节，溯源越快。当事方想回应或提供反证，也欢迎通过这里联系我们。"
          />

          <form
            id="verify-contact-form"
            data-readdy-form={FORM_ADDR}
            onSubmit={onSubmit}
            className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2"
          >
            <div>
              <label
                htmlFor="cf-name"
                className="mb-1.5 block text-xs font-medium text-foreground-800"
              >
                怎么称呼你
              </label>
              <input
                id="cf-name"
                name="name"
                type="text"
                autoComplete="name"
                placeholder="昵称即可"
                className={inputCls}
              />
            </div>
            <div>
              <label
                htmlFor="cf-email"
                className="mb-1.5 block text-xs font-medium text-foreground-800"
              >
                联系邮箱 <span className="text-primary-600">*</span>
              </label>
              <input
                id="cf-email"
                name="email"
                type="email"
                required
                autoComplete="email"
                placeholder="you@example.com"
                className={inputCls}
              />
            </div>
            <div className="sm:col-span-2">
              <label
                htmlFor="cf-subject"
                className="mb-1.5 block text-xs font-medium text-foreground-800"
              >
                来意类型 <span className="text-primary-600">*</span>
              </label>
              <select
                id="cf-subject"
                name="subject"
                required
                defaultValue="我要求证一条爆料"
                className={`${inputCls} appearance-none bg-no-repeat bg-[right_1rem_center] bg-[length:1rem]`}
                style={{
                  backgroundImage:
                    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%235c5348' stroke-width='2'><path d='M6 9l6 6 6-6'/></svg>\")",
                }}
              >
                <option>我要求证一条爆料</option>
                <option>我来补充线索 / 提供原始素材</option>
                <option>我是当事方 / 想提交反证回应</option>
                <option>媒体合作 / 转载授权</option>
                <option>其他</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <div className="flex items-center justify-between">
                <label
                  htmlFor="cf-message"
                  className="mb-1.5 block text-xs font-medium text-foreground-800"
                >
                  详细描述 <span className="text-primary-600">*</span>
                </label>
                <span className="text-xs text-foreground-500">{len} / 500</span>
              </div>
              <textarea
                id="cf-message"
                name="message"
                required
                rows={6}
                maxLength={500}
                onChange={(e) => setLen(e.target.value.length)}
                placeholder="请尽量说明：事件对象、发生时间、你看到的原始出处或截图链接、以及你认为最可疑/最可信的一点…"
                className={`${inputCls} resize-none`}
              />
            </div>

            {/* 反垃圾蜜罐字段 */}
            <div className="form-guard" aria-hidden="true">
              <label htmlFor="cf-company">公司</label>
              <input
                id="cf-company"
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                readOnly
              />
            </div>

            <div className="flex flex-col gap-3 sm:col-span-2 sm:flex-row sm:items-center">
              <button
                type="submit"
                disabled={sending}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-7 py-3 text-sm text-background-50 hover:bg-primary-600 disabled:opacity-60"
              >
                <i className="ri-send-plane-line" />
                {sending ? "提交中…" : "提交线索"}
              </button>
              <p className="text-xs text-foreground-500">
                提交即表示你同意我们仅将信息用于核验工作，不公开你的身份与隐私。
              </p>
            </div>

            {status.type === "success" && (
              <p
                role="status"
                className="sm:col-span-2 rounded-xl border border-accent-300 bg-accent-100/60 px-4 py-3 text-sm text-accent-900"
              >
                <i className="ri-check-double-line mr-1.5" />
                {status.text}
              </p>
            )}
            {status.type === "error" && (
              <p
                role="alert"
                className="sm:col-span-2 rounded-xl border border-primary-300 bg-primary-100/60 px-4 py-3 text-sm text-primary-900"
              >
                <i className="ri-error-warning-line mr-1.5" />
                {status.text}
              </p>
            )}
          </form>
        </div>

        {/* 右侧联系信息 */}
        <aside>
          <div className="rounded-2xl border border-background-200 bg-background-100 p-6 lg:sticky lg:top-24">
            <h3 className="text-lg font-semibold text-foreground-950">联系我们</h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              无论是爆料、补料、回应还是合作，都欢迎来信。编辑部会逐条登记并回复。
            </p>
            <ul className="mt-5 space-y-4">
              <li className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700 text-lg">
                  <i className="ri-mail-send-line" />
                </span>
                <div>
                  <p className="text-xs text-foreground-500">投稿 / 求证</p>
                  <p className="text-sm font-medium text-foreground-950 break-all">
                    tip@guaheshe.example
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-700 text-lg">
                  <i className="ri-shake-hands-line" />
                </span>
                <div>
                  <p className="text-xs text-foreground-500">商务 / 转载合作</p>
                  <p className="text-sm font-medium text-foreground-950 break-all">
                    partner@guaheshe.example
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-secondary-800 text-lg">
                  <i className="ri-time-line" />
                </span>
                <div>
                  <p className="text-xs text-foreground-500">平均回复时长</p>
                  <p className="text-sm font-medium text-foreground-950">
                    工作日 24 小时内
                  </p>
                </div>
              </li>
            </ul>
            <div className="mt-6 flex items-center gap-2 border-t border-background-200 pt-5">
              <span className="text-xs text-foreground-500">关注我们</span>
              {["ri-weibo-line", "ri-wechat-2-line", "ri-community-line"].map(
                (ic) => (
                  <a
                    key={ic}
                    href="#contact"
                    aria-label="社交账号"
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-background-50 text-foreground-700 hover:bg-primary-500 hover:text-background-50 transition-colors"
                  >
                    <i className={ic} />
                  </a>
                ),
              )}
            </div>
          </div>
        </aside>
      </div>
    </SectionShell>
  );
}